/**
 * DemandPlanningTimeline Component
 *
 * Main view for demand planning, showing aggregated demand forecast
 * by week and strain. Provides visibility into future demand from
 * recurring orders and AI predictions.
 *
 * @module DemandPlanningTimeline
 */

import { useState } from 'react'
import { RefreshCw, Loader2, TrendingUp, Repeat } from 'lucide-react'
import { PageLayout } from '../layout'
import { Button } from '@/components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import DemandWeekRow from './DemandWeekRow'
import { useDemandPlanningData } from './useDemandPlanningData'

/**
 * Week range options
 */
const WEEK_OPTIONS = [
  { value: '4', label: '4 weeks' },
  { value: '8', label: '8 weeks' },
  { value: '12', label: '12 weeks' },
  { value: '26', label: '26 weeks' }
]

/**
 * DemandPlanningTimeline - Main demand planning view
 *
 * @param {Object} props
 * @param {Object} props.user - Current user object
 * @param {Function} props.onLogout - Logout handler
 */
export default function DemandPlanningTimeline({ user, onLogout }) {
  const [weeksAhead, setWeeksAhead] = useState(12)

  const { weeklyDemand, loading, error, refresh } = useDemandPlanningData({
    weeksAhead
  })

  // Calculate summary stats
  const totalRecurringKg = weeklyDemand.reduce(
    (sum, week) => sum + week.items.reduce((s, item) => s + item.recurringKg, 0),
    0
  )
  const totalPredictedKg = weeklyDemand.reduce(
    (sum, week) => sum + week.items.reduce((s, item) => s + item.predictedKg, 0),
    0
  )
  const totalDemandKg = totalRecurringKg + totalPredictedKg

  return (
    <PageLayout
      title="Demand Planning"
      user={user}
      onLogout={onLogout}
      headerActions={
        <>
          <Select
            value={String(weeksAhead)}
            onValueChange={(v) => setWeeksAhead(Number(v))}
          >
            <SelectTrigger className="w-32">
              <SelectValue placeholder="Weeks ahead" />
            </SelectTrigger>
            <SelectContent>
              {WEEK_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            onClick={refresh}
            disabled={loading}
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4" />
            )}
          </Button>
        </>
      }
    >
      <div className="space-y-6">
        {/* Summary Stats */}
        {!loading && weeklyDemand.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="flex items-center gap-2 text-gray-600 mb-1">
                <Repeat className="w-4 h-4 text-blue-500" />
                <span className="text-sm">Recurring Orders</span>
              </div>
              <p className="text-2xl font-semibold text-gray-900">
                {totalRecurringKg.toFixed(1)}kg
              </p>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="flex items-center gap-2 text-gray-600 mb-1">
                <TrendingUp className="w-4 h-4 text-purple-500" />
                <span className="text-sm">AI Predictions</span>
              </div>
              <p className="text-2xl font-semibold text-gray-900">
                {totalPredictedKg.toFixed(1)}kg
              </p>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="flex items-center gap-2 text-gray-600 mb-1">
                <span className="text-sm">Total Forecast</span>
              </div>
              <p className="text-2xl font-semibold text-gray-900">
                {totalDemandKg.toFixed(1)}kg
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Over {weeksAhead} weeks
              </p>
            </div>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            <span className="ml-3 text-gray-500">Loading demand forecast...</span>
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
            <p className="text-red-700">Failed to load demand forecast</p>
            <p className="text-sm text-red-500 mt-1">{error.message}</p>
            <Button
              variant="outline"
              size="sm"
              onClick={refresh}
              className="mt-3"
            >
              Try Again
            </Button>
          </div>
        )}

        {/* Empty State */}
        {!loading && !error && weeklyDemand.length === 0 && (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
            <TrendingUp className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No demand forecast</h3>
            <p className="text-gray-500">
              No recurring orders or predictions for the next {weeksAhead} weeks.
            </p>
          </div>
        )}

        {/* Weekly Demand Rows */}
        {!loading && !error && weeklyDemand.length > 0 && (
          <div className="space-y-4">
            {weeklyDemand.map((week) => (
              <DemandWeekRow
                key={week.weekStart}
                weekLabel={week.weekLabel}
                weekStartDate={week.weekStartDate}
                items={week.items}
                totalKg={week.totalKg}
              />
            ))}
          </div>
        )}
      </div>
    </PageLayout>
  )
}
